import React, { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

import CardMedia from '@mui/material/CardMedia';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import MuiCard from '@mui/material/Card';
import Box from '@mui/material/Box';

import { InView } from 'react-intersection-observer';
import { useTranslation } from 'next-i18next';

import Divider from 'src/components/ui/Divider';
import Author from 'src/components/news/posts/Author';
import { getTheFirstUrlFromString } from 'src/utils/getTheFirstUrlFromString';
import InteractionsBar from 'src/components/news/InteractionsBar';
import PostMenu from 'src/components/news/posts/PostMenu';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import ExpandableText from 'src/components/ui/ExpandableText';
import FollowUnFollowButton from 'src/components/ui/FollowUnFollowButton';

import type { Owner, Ad, NewsCategory, Medium, Post } from 'src/types/__generated__/graphql';
import {
    useAdLightQuery,
    useNewsPostQuery,
    useAddContentEventMutation,
    useProjectQuery,
    useParsedLinkQuery,
} from 'src/hooks/__generated__/queries';

import { routePathAd, routePathNewsPost, routePathUser } from 'src/constants/router';

import useUser from 'src/hooks/useUser';
import useAppRouter from 'src/hooks/useAppRouter';

import theme from 'src/utils/theme';
import formatPrice from 'src/utils/formatPrice';
import { getFormattedMessageWithMentions } from 'src/utils/mentionsUtils';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent, ContextFreeEventPrefixEnums, EventParamKeys } from 'src/utils/analytics';
import useDynamicLogEvents, { AnalyticsContextProvider } from 'src/hooks/useDynamicLogEvents';
import useBreakpoints from 'src/hooks/useBreakpoints';

const PostModal = dynamic(() => import('src/components/news/posts/PostModal'), { ssr: false });
const LikesModal = dynamic(() => import('src/components/news/LikesModal'), { ssr: false });

const StyledCard = styledComponent(MuiCard)<{
    $backgroundColor: string;
    $showHeader?: boolean;
}>`
    .MuiCardContent-root,
    .MuiCardActions-root,
    .MuiCardHeader-root {
        background-color: ${({ $backgroundColor }) => $backgroundColor};
    }
    background-color: ${({ $backgroundColor }) => $backgroundColor};

    .MuiCardHeader-root {
        cursor: pointer;
    }

    .MuiCardContent-root {
        padding-top: ${({ $showHeader }) => ($showHeader ? '0' : '1rem')};
    }
    .MuiCardHeader-content {
        flex: 1 1 auto;
        display: block;
        width: calc(100% - 12rem); // avatar + buttons width
    }
    .MuiCardHeader-content p {
        cursor: pointer;
    }
    .MuiCardHeader-avatar {
        cursor: pointer;
    }
    .MuiCardHeader-action {
        margin: 0;
    }
`;

const StyledTinyPreviewCardContainer = styledComponent(Box)`
    margin-bottom: 1rem;
    margin-left: 1rem;
    margin-right: 1rem;
`;

enum Modal {
    none = 'None',
    postModal = 'PostModal',
    likesModal = 'LikesModal',
}

type Props = {
    carousel: React.ComponentType<{ post: Post }> | null;
    post: Post;
    showHeader?: boolean;
    backgroundColor?: string;
    onVisibilityChange?: (isVisible: boolean) => void;
};
const PostCard: React.FC<Props> = ({
    carousel,
    post,
    showHeader = true,
    backgroundColor = theme.palette.background.paper,
    onVisibilityChange,
}) => {
    const { t, i18n } = useTranslation(['ad', 'news']);
    const [modalOpened, setModalOpened] = useState(Modal.none);
    const handleModalClose = () => setModalOpened(Modal.none);
    const currentUser = useUser();
    const router = useAppRouter();
    const isInFavoritePage = ['/favorites/[id]', '/galleries/[galleryId]'].includes(router.pathname);
    const isOwner = post.owner && post.owner.user?.id === currentUser?.id;
    const { logEventDynamic } = useDynamicLogEvents();

    const { isMobile } = useBreakpoints();

    const actions = useMemo(
        () => (
            <Box display="flex" flexDirection="row">
                {!isOwner && post.owner?.user && !isMobile && !isInFavoritePage && (
                    <FollowUnFollowButton
                        user={post.owner.user}
                        followButtonProps={{ 'data-target-id': 'postCard-followUnfollowButton' }}
                    />
                )}
                {currentUser && <PostMenu post={post} />}
            </Box>
        ),
        [currentUser, isInFavoritePage, isOwner, isMobile, post],
    );

    const { data: newsPostData } = useNewsPostQuery({
        variables: {
            newsPostId: String(post.newsPost?.id),
        },
        skip: !post.newsPost?.id,
    });

    const { data: adData } = useAdLightQuery({
        variables: {
            id: String(post.ad?.id),
        },
        skip: !post.ad?.id,
    });

    const { data: projectData } = useProjectQuery({
        variables: {
            projectId: String(post.project?.id),
        },
        skip: !post.project?.id,
    });

    const [addContentEventMutation] = useAddContentEventMutation();
    const handleVisibilityChange = (isVisible: boolean) => {
        if (isVisible) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            addContentEventMutation({
                variables: {
                    postId: post.id,
                    eventType: 'impressed',
                },
                fetchPolicy: 'no-cache',
            });
        }

        onVisibilityChange?.(isVisible);
    };

    const firstUrlFromString = useMemo(
        () => (post.message ? getTheFirstUrlFromString(post.message) : undefined),
        [post.message],
    );
    const formattedContentWithMentionsLinks = useMemo(
        () => (post.message ? getFormattedMessageWithMentions(post.message) : undefined),
        [post.message],
    );

    const carouselMemo = useMemo(() => {
        const Carousel = carousel;

        return post.media.length > 0 && Carousel ? (
            <CardMedia data-target-id="post-media" onClick={() => setModalOpened(Modal.postModal)}>
                <Carousel post={post} />
            </CardMedia>
        ) : (
            <Divider />
        );
    }, [carousel, post]);

    const { data: linkData } = useParsedLinkQuery({
        variables: {
            link: firstUrlFromString as string,
        },
        skip: !firstUrlFromString,
    });

    const linkPublisher = linkData?.parsedLink?.publisher;
    const linkTitle = linkData?.parsedLink?.title;
    const price = formatPrice({
        price: Number(adData?.ad?.price ?? adData?.ad?.rent),
        lang: i18n.language,
    });

    const handleClickOnProject = async () => {
        await router.push(
            `${routePathUser}/${projectData?.project?.project?.owner.user?.id}/projects/?project=${projectData?.project?.project?.id}`,
        );
    };

    return (
        <AnalyticsContextProvider
            value={{
                eventNamePrefix: ContextFreeEventPrefixEnums.POST,
                [EventParamKeys.ItemId]: post.id,
            }}
        >
            <InView onChange={handleVisibilityChange}>
                <StyledCard key={post.id} elevation={0} $backgroundColor={backgroundColor} $showHeader={showHeader}>
                    {showHeader && (
                        <CardHeader
                            onClick={() => setModalOpened(Modal.postModal)}
                            data-target-id="post-author"
                            style={{ cursor: 'pointer' }}
                            title={<Author owner={post.owner as Owner} createdAt={post.createdAt as string} />}
                            action={actions}
                        />
                    )}
                    {post.message && (
                        <CardContent
                            id={`card-content-${post.id}`}
                            data-target="post-content"
                            data-target-id={`post-content-id-${post.id}`}
                            onClick={() => {
                                logEventDynamic(AnalyticsEvent.See);
                                setModalOpened(Modal.postModal);
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <ExpandableText text={formattedContentWithMentionsLinks} lines={3} enableLinkify />
                        </CardContent>
                    )}
                    {newsPostData && (
                        <StyledTinyPreviewCardContainer>
                            <TinyPreviewCard
                                iconName="NewsIcon"
                                type="newsPost"
                                data-target-id="post-linked-news-post-card"
                                onClick={() => router.push(`${routePathNewsPost}/${newsPostData.newsPost?.id}`)}
                                tag={newsPostData.newsPost?.featuredCategory as NewsCategory}
                                media={newsPostData.newsPost?.media as Medium[]}
                                subtitle={newsPostData.newsPost?.title as string}
                            />
                        </StyledTinyPreviewCardContainer>
                    )}
                    {projectData && (
                        <StyledTinyPreviewCardContainer>
                            <TinyPreviewCard
                                iconName="ProjectIcon"
                                type="project"
                                data-target-id="post-linked-project-card"
                                onClick={handleClickOnProject}
                                title={t('news:project')}
                                media={projectData.project?.project?.media as Medium[]}
                                subtitle={projectData.project?.project?.name}
                            />
                        </StyledTinyPreviewCardContainer>
                    )}
                    {firstUrlFromString && linkPublisher && linkTitle && !newsPostData && !adData && !projectData && (
                        <StyledTinyPreviewCardContainer>
                            <TinyPreviewCard
                                iconName="LinkIcon"
                                type="link"
                                onClick={() => router.push(firstUrlFromString)}
                                mediaUrl={linkData.parsedLink?.image as string}
                                subtitle={linkTitle}
                                title={linkPublisher}
                            />
                        </StyledTinyPreviewCardContainer>
                    )}

                    {adData && (
                        <StyledTinyPreviewCardContainer>
                            <TinyPreviewCard
                                iconName="AdIcon"
                                ad={adData.ad as Ad}
                                type="ad"
                                data-target-id="post-linked-ad-card"
                                onClick={() => router.push(`${routePathAd}/${adData.ad?.id}`)}
                                media={adData.ad?.media as Medium[]}
                                subtitle={price}
                                title={t(`ad:propertyTypes.${adData.ad?.propertyType}`)}
                            />
                        </StyledTinyPreviewCardContainer>
                    )}
                    {carouselMemo}
                    <CardActions data-target-id="post-cardActions">
                        <InteractionsBar
                            post={post}
                            onCommentsClick={() => setModalOpened(Modal.postModal)}
                            onLikesClick={() => setModalOpened(Modal.likesModal)}
                        />
                        {modalOpened === Modal.postModal && <PostModal onClose={handleModalClose} post={post} open />}
                        {modalOpened === Modal.likesModal && (
                            <LikesModal
                                onClose={handleModalClose}
                                postId={post.id}
                                numberOfLikes={post.totalLikes}
                                open
                            />
                        )}
                    </CardActions>
                </StyledCard>
            </InView>
        </AnalyticsContextProvider>
    );
};

export default PostCard;
